import i18nService from '../../../services/i18nService';
import qtiConstants from '../../../qti/Constants';
import loadingTools from '../loadingTools';
import ckeditorService from '../../../services/CKEditorService';
import marketplacePropertiesService from '../../../services/MarketplacePropertiesService';
import { reportError } from '../../../services/ErrorReporting';

const { CKEDITOR_LATEST, WEBSPELLCHECKER_WEB_LATEST } = qtiConstants.staticLibPaths;
const tag = '?t=I63C';

function getSameOriginRoot() {
    try {
        // Prefix with /player/file/ci so the URL:
        // 1. Falls within the service worker's scope (/player/)
        // 2. Matches the SW's handleFetch pattern (checks for '/file/ci' in URL)
        return `/player/file/ci${new URL(CKEDITOR_LATEST).pathname}`;
    } catch (e) {
        return CKEDITOR_LATEST;
    }
}

const CKEditorLoader = {
    getCoreFile() {
        return { url: `${CKEDITOR_LATEST}ckeditor.js` };
    },

    getCachedCoreUrl() {
        return `${getSameOriginRoot()}ckeditor.js`;
    },

    getExtraFiles() {
        const files = [
            { url: `${CKEDITOR_LATEST}skins/moono-lisa/images/close.png`, type: 'image' },
            { url: `${CKEDITOR_LATEST}skins/moono-lisa/icons.png?t=de1371d`, type: 'image' },
            { url: `${CKEDITOR_LATEST}skins/moono-lisa/icons_hidpi.png?t=de1371d`, type: 'image' },
            { url: `${CKEDITOR_LATEST}skins/moono-lisa/dialog.css${tag}`, type: 'css' },
            { url: `${CKEDITOR_LATEST}skins/moono-lisa/editor.css${tag}`, type: 'css' },
            { url: `${CKEDITOR_LATEST}skins/moono-lisa/editor_gecko.css${tag}`, type: 'css' },
            { url: `${CKEDITOR_LATEST}plugins/wordcount/css/wordcount.css`, type: 'css' },
            { url: `${CKEDITOR_LATEST}plugins/table/dialogs/table.js${tag}`, type: 'js' },
            { url: `${CKEDITOR_LATEST}plugins/specialchar/dialogs/specialchar.js${tag}`, type: 'js' },
            { url: `${CKEDITOR_LATEST}plugins/a11yhelp/plugin.js${tag}`, type: 'js' },
            { url: `${CKEDITOR_LATEST}plugins/a11yhelp/dialogs/a11yhelp.js${tag}`, type: 'js' },
            { url: `${CKEDITOR_LATEST}plugins/justify/plugin.js${tag}`, type: 'js' },
            { url: `${CKEDITOR_LATEST}styles.js${tag}`, type: 'js' },
            { url: `${CKEDITOR_LATEST}plugins/magicline/images/icon.png?t=de1371d`, type: 'image' },
            { url: `${CKEDITOR_LATEST}plugins/icons.png?t=de1371d`, type: 'image' },
            { url: `${CKEDITOR_LATEST}plugins/dialog/styles/dialog.css`, type: 'css' },
            { url: `${CKEDITOR_LATEST}contents.css${tag}`, type: 'css' },
        ];

        this.addConditionalPluginFiles(files);
        this.addCaptionFiles(files);

        return files;
    },

    addConditionalPluginFiles(files) {
        if (ckeditorService.shouldUseIndentPlugin()) {
            files.push({ url: `${CKEDITOR_LATEST}plugins/indent/plugin.js${tag}`, type: 'js' });
            files.push({ url: `${CKEDITOR_LATEST}plugins/indentblock/plugin.js${tag}`, type: 'js' });
        }
    },

    addCaptionFiles(files) {
        i18nService
            .getAvailableLanguages()
            .forEach((lang) => {
                const langCode = i18nService.getIetfLanguageCode(lang);
                const ckLang = i18nService.languageMapIetfToCKEditor[langCode];
                if (ckLang !== undefined) {
                    files.push({ url: `${CKEDITOR_LATEST}lang/${ckLang}.js${tag}`, type: 'js' });
                    files.push({ url: `${CKEDITOR_LATEST}plugins/specialchar/dialogs/lang/${ckLang}.js${tag}`, type: 'js' });
                    files.push({ url: `${CKEDITOR_LATEST}plugins/a11yhelp/dialogs/lang/${ckLang}.js${tag}`, type: 'js' });
                }
            });
    },

    getAllFilesToCache() {
        const files = [this.getCoreFile()];
        files.push(...this.getExtraFiles());
        return files;
    },

    /**
     * Returns all CKEditor files as {fetchUrl, url, type} objects where:
     * - fetchUrl: CDN URL used to fetch the file (includes version-specific query tags)
     * - url: same-origin cache key under /player/file/ci/... for SW interception
     *
     * The same-origin root is derived from CKEDITOR_LATEST so version updates
     * in Constants.js automatically update all cache keys — no hardcoding needed.
     */
    getAllFilesToCacheWithSameOrigin() {
        const sameOriginRoot = getSameOriginRoot();
        return this.getAllFilesToCache().map(f => ({
            fetchUrl: f.url,
            url: f.url.replace(CKEDITOR_LATEST, sameOriginRoot),
            type: f.type,
        }));
    },

    /**
     * Proactively caches all CKEditor files into GENERAL_RESOURCES Cache Storage
     * so the service worker can serve them offline.
     *
     * Files are fetched from CDN (fetchUrl) and stored under same-origin keys (url)
     * that the service worker recognises via the /file/ci/ URL pattern.
     * Query tags (e.g. ?t=I63C) are preserved in both fetchUrl and url so that
     * CKEditor's internal CKEDITOR.getUrl() calls resolve to exact cache matches.
     *
     * window.CKEDITOR_BASEPATH must be set to getSameOriginRoot() before ckeditor.js
     * loads so CKEditor resolves all internal resource URLs to the same-origin path.
     */
    async cacheFiles() {
        const files = this.getAllFilesToCacheWithSameOrigin();

        if (!('caches' in window)) {
            return;
        }

        try {
            const cache = await caches.open('GENERAL_RESOURCES');

            await Promise.all(files.map(async (file) => {
                try {
                    // Use ignoreVary so we can detect entries previously cached
                    // with a Vary header that need replacing.
                    const existing = await cache.match(file.url, { ignoreVary: true });
                    if (existing && !existing.headers.has('vary')) {
                        return;
                    }

                    let response;
                    try {
                        response = await fetch(file.fetchUrl);
                    } catch (err) {
                        response = await fetch(file.fetchUrl, { mode: 'no-cors' });
                    }

                    if (response.ok) {
                        // Strip the Vary header before storing. The CDN returns
                        // "Vary: Origin, accept-encoding" which causes cache.match() to fail
                        // in the service worker: the stored request (created from a URL string)
                        // has no Accept-Encoding header, but a real fetch request does,
                        // so the Vary check never matches and the SW falls through to the network.
                        const headers = new Headers(response.headers);
                        headers.delete('vary');

                        await cache.put(file.url, new Response(response.body, {
                            status: response.status,
                            statusText: response.statusText,
                            headers,
                        }));
                    } else if (response.type === 'opaque') {
                        // Opaque responses cannot be reconstructed — store directly.
                        await cache.put(file.url, response);
                    }
                } catch (err) {
                    reportError(err);
                }
            }));
        } catch (err) {
            // caches.open() can reject in private browsing mode or when storage quota is exceeded.
            // Treat as best-effort so rejections do not surface as unhandled promise rejections.
            reportError(err);
        }
    },

    patchScriptLoader(cache) {
        if (!window.CKEDITOR || !window.CKEDITOR.scriptLoader) {
            return;
        }
        // Guard: only patch once per CKEDITOR instance
        if (window.CKEDITOR.offlineScriptLoaderPatched) {
            return;
        }
        window.CKEDITOR.offlineScriptLoaderPatched = true;

        const scriptLoader = window.CKEDITOR.scriptLoader;
        const originalLoad = scriptLoader.load;

        // Replace scriptLoader.load so every dynamic script CKEditor requests
        // (lang files, styles.js, plugins) is served from GENERAL_RESOURCES cache
        // as a blob URL rather than going to the network (which fails when offline).
        // ignoreSearch: true handles the version-tag mismatch between CKEditor's
        // internal timestamp (?t=L0QD) and our cached key (?t=I63C).
        scriptLoader.load = function cacheFirstLoad(url, callback, scope, showBusy) {
            // CKEditor passes arrays of URLs for batch loading (e.g. plugins, lang files).
            // Process each element through cacheFirstLoad so they are served from cache.
            if (Array.isArray(url)) {
                if (url.length === 0) {
                    // Let original handle empty arrays — it manages the callback semantics correctly
                    originalLoad.call(scriptLoader, url, callback, scope, showBusy);
                    return;
                }
                // For array calls, CKEditor's resourceManager.load passes an innerCb that expects
                // (completedUrls[], failedUrls[]) — the same contract as the original scriptLoader.
                // Passing a boolean true causes innerCb to crash when it tries to look up
                // loading[name] keyed by URL, since it receives a non-iterable value.
                const completedUrls = [];
                const failedUrls = [];
                let remaining = url.length;
                url.forEach((u) => {
                    cacheFirstLoad(u, (success) => {
                        if (success) completedUrls.push(u);
                        else failedUrls.push(u);
                        if (--remaining === 0 && callback) {
                            callback.call(scope || window, completedUrls, failedUrls);
                        }
                    }, scope, showBusy);
                });
                return;
            }
            // CKEditor occasionally passes other non-string values — fall back to original
            if (typeof url !== 'string') {
                originalLoad.call(scriptLoader, url, callback, scope, showBusy);
                return;
            }
            // Map CDN URLs to same-origin cache keys; same-origin paths pass through unchanged
            const cacheKey = url.replace(CKEDITOR_LATEST, getSameOriginRoot());
            cache.match(cacheKey, { ignoreSearch: true })
                .then(async (response) => {
                    if (!response) {
                        originalLoad.call(scriptLoader, url, callback, scope, showBusy);
                        return;
                    }
                    try {
                        const blob = await response.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const script = document.createElement('script');
                        script.src = blobUrl;
                        script.onload = () => {
                            URL.revokeObjectURL(blobUrl);
                            if (callback) callback.call(scope || window, true);
                        };
                        script.onerror = () => {
                            URL.revokeObjectURL(blobUrl);
                            originalLoad.call(scriptLoader, url, callback, scope, showBusy);
                        };
                        document.head.appendChild(script);
                    } catch (e) {
                        reportError(e);
                        originalLoad.call(scriptLoader, url, callback, scope, showBusy);
                    }
                })
                .catch(() => originalLoad.call(scriptLoader, url, callback, scope, showBusy));
        };
    },

    /**
     * Fetches every image stored in GENERAL_RESOURCES cache and converts it to a
     * data URL, keyed by absolute path (no query string).  This map is built once
     * before the patches run so that CSS url() replacements can be done synchronously.
     */
    async buildImageDataUrls(cache) {
        const dataUrls = new Map();
        const keys = await cache.keys();
        await Promise.all(
            keys
                .filter(req => /\.(png|jpg|jpeg|gif|svg|webp)$/i.test(req.url.split('?')[0]))
                .map(async (req) => {
                    try {
                        const response = await cache.match(req.url);
                        if (!response) return;
                        const blob = await response.blob();
                        const dataUrl = await new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result);
                            reader.readAsDataURL(blob);
                        });
                        // Key without query so ignoreSearch-style lookups work
                        dataUrls.set(req.url.split('?')[0], dataUrl);
                    } catch (e) {
                        // skip individual image failures — non-critical
                    }
                })
        );
        return dataUrls;
    },

    /**
     * Rewrites url() references in CSS text:
     * - Relative paths are resolved to absolute using cssBase.
     * - If the resolved path is in imageDataUrls, the reference is replaced with a
     *   data URL so the image is served from memory without any network request.
     * - Otherwise the absolute URL is kept (for SW to serve in production).
     */
    resolveUrlsInCss(cssText, cssBase, imageDataUrls) {
        return cssText.replace(
            /url\(\s*(?:"([^"]+)"|'([^']+)'|([^'"\s)]+))\s*\)/g,
            (match, dq, sq, unquoted) => {
                // eslint-disable-next-line no-nested-ternary
                const rel = dq !== undefined ? dq : (sq !== undefined ? sq : unquoted);
                const isAbsolute = rel.startsWith('https:') || rel.startsWith('http:')
                    || rel.startsWith('data:') || rel.startsWith('blob:') || rel.startsWith('#');
                if (isAbsolute) return match;
                try {
                    const absUrl = new URL(rel, cssBase).href;
                    const pathKey = absUrl.split('?')[0];
                    const dataUrl = imageDataUrls.get(pathKey);
                    return dataUrl ? `url(${dataUrl})` : `url(${absUrl})`;
                } catch (e) {
                    return match;
                }
            }
        );
    },

    patchAppendStyleSheet(cache, imageDataUrls) {
        if (!window.CKEDITOR || !window.CKEDITOR.dom || !window.CKEDITOR.dom.document) {
            return;
        }
        if (window.CKEDITOR.offlineStyleSheetPatched) {
            return;
        }
        window.CKEDITOR.offlineStyleSheetPatched = true;

        const sameOriginRoot = getSameOriginRoot();
        const originalFn = window.CKEDITOR.dom.document.prototype.appendStyleSheet;

        // Replace appendStyleSheet so CKEditor skin/plugin CSS is served from
        // GENERAL_RESOURCES cache as an inline <style> tag when offline.
        // url() references are resolved to data URLs (from imageDataUrls) so that
        // background images (toolbar icons, sprites) work without network access.
        window.CKEDITOR.dom.document.prototype.appendStyleSheet = function cacheFirstStyleSheet(url) {
            const cacheKey = url.replace(CKEDITOR_LATEST, sameOriginRoot);
            cache.match(cacheKey, { ignoreSearch: true })
                .then(async (response) => {
                    if (!response) {
                        originalFn.call(this, url);
                        return;
                    }
                    try {
                        const strippedKey = cacheKey.split('?')[0];
                        const cssBase = strippedKey.substring(0, strippedKey.lastIndexOf('/') + 1);
                        const cssText = await response.text();
                        const resolved = CKEditorLoader.resolveUrlsInCss(cssText, cssBase, imageDataUrls);
                        // Use this.$ (the underlying DOM document) so CSS is injected into the
                        // correct document — appendStyleSheet may be called on an iframe document
                        // (e.g. the richcombo floatPanel) and `document` (global) would be wrong.
                        const targetDoc = this.$;
                        const targetHead = targetDoc.head || targetDoc.getElementsByTagName('head')[0];
                        if (!targetHead) {
                            originalFn.call(this, url);
                            return;
                        }
                        const style = targetDoc.createElement('style');
                        style.textContent = resolved;
                        targetHead.appendChild(style);
                    } catch (e) {
                        reportError(e);
                        originalFn.call(this, url);
                    }
                })
                .catch(() => originalFn.call(this, url));
        };
    },

    patchAppendStyleText(imageDataUrls) {
        if (!window.CKEDITOR || !window.CKEDITOR.dom || !window.CKEDITOR.dom.document) {
            return;
        }
        if (window.CKEDITOR.offlineStyleTextPatched) {
            return;
        }
        window.CKEDITOR.offlineStyleTextPatched = true;

        const originalFn = window.CKEDITOR.dom.document.prototype.appendStyleText;

        // CKEditor injects toolbar icon CSS via appendStyleText.  The URLs in that
        // CSS are already absolute (built with CKEDITOR_BASEPATH = getSameOriginRoot()),
        // so we only need to swap same-origin image paths for data URLs.
        window.CKEDITOR.dom.document.prototype.appendStyleText = function cacheFirstStyleText(cssText) {
            const resolved = cssText.replace(
                /url\(\s*(?:"([^"]+)"|'([^']+)'|([^'"\s)]+))\s*\)/g,
                (match, dq, sq, unquoted) => {
                    // eslint-disable-next-line no-nested-ternary
                    const url = dq !== undefined ? dq : (sq !== undefined ? sq : unquoted);
                    if (url.startsWith('data:')) return match;
                    const pathKey = url.split('?')[0];
                    const dataUrl = imageDataUrls.get(pathKey);
                    return dataUrl ? `url(${dataUrl})` : match;
                }
            );
            return originalFn.call(this, resolved);
        };
    },

    patchPanelIframes(cache, imageDataUrls) {
        if (!('MutationObserver' in window)) {
            return;
        }
        const self = this;
        const sameOriginRoot = getSameOriginRoot();

        async function injectCssIntoIframeDoc(iframeDoc) {
            if (!iframeDoc || !iframeDoc.head) return;
            const links = Array.from(iframeDoc.querySelectorAll('link[rel="stylesheet"]'));
            await Promise.all(links.map(async (link) => {
                const url = link.href;
                if (!url) return;
                const cacheKey = url.replace(CKEDITOR_LATEST, sameOriginRoot);
                try {
                    const response = await cache.match(cacheKey, { ignoreSearch: true });
                    if (!response) return;
                    const strippedCacheKey = cacheKey.split('?')[0];
                    const cssBase = strippedCacheKey.substring(0, strippedCacheKey.lastIndexOf('/') + 1);
                    const cssText = await response.text();
                    if (!cssText) return;
                    const resolved = self.resolveUrlsInCss(cssText, cssBase, imageDataUrls);
                    const style = iframeDoc.createElement('style');
                    style.textContent = resolved;
                    iframeDoc.head.appendChild(style);
                    // Disable the original link so it does not keep retrying the network
                    link.disabled = true;
                } catch (e) {
                    // best-effort — ignore individual link failures
                }
            }));
        }

        function handleIframe(iframe) {
            // CKEditor writes HTML into panel iframes via document.write after load.
            // Listen for both the immediate load and a deferred tick to cover both cases.
            const tryInject = () => {
                const doc = iframe.contentDocument;
                if (doc && doc.readyState !== 'loading') {
                    injectCssIntoIframeDoc(doc);
                }
            };
            iframe.addEventListener('load', () => {
                injectCssIntoIframeDoc(iframe.contentDocument);
                // Deferred tick handles the case where CKEditor writes content post-load
                setTimeout(() => injectCssIntoIframeDoc(iframe.contentDocument), 0);
            });
            tryInject();
            setTimeout(tryInject, 0);
        }

        const observer = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                for (const node of mutation.addedNodes) {
                    if (node.nodeType === 1) {
                        if (node.tagName === 'IFRAME') {
                            handleIframe(node);
                        } else {
                            node.querySelectorAll('iframe').forEach(handleIframe);
                        }
                    }
                }
            }
        });

        observer.observe(document.body || document.documentElement, {
            childList: true,
            subtree: true,
        });
    },

    async loadCoreFromCache() {
        if (!('caches' in window)) {
            return;
        }
        try {
            const cache = await caches.open('GENERAL_RESOURCES');
            const coreUrl = this.getCachedCoreUrl();

            // Step 1: load ckeditor.js from cache as a blob URL (SW-independent)
            const coreResponse = await cache.match(coreUrl, { ignoreSearch: true });
            if (!coreResponse) {
                return;
            }
            const coreBlob = await coreResponse.blob();
            const coreBlobUrl = URL.createObjectURL(coreBlob);
            await loadingTools.loadLibrary(coreBlobUrl, 'CKEDITOR');
            URL.revokeObjectURL(coreBlobUrl);

            if (!window.CKEDITOR) {
                return;
            }

            // Step 2: patch CKEDITOR.scriptLoader.load so that all subsequent dynamic script
            // loading by CKEditor (lang files at the correct time after plugins register,
            // plugin scripts) is served from GENERAL_RESOURCES cache via blob URLs.
            // Pre-loading lang files upfront caused "langEntries null" errors because plugin
            // lang files ran before their plugins were registered. The patch lets CKEditor
            // load each resource exactly when it needs it — just from cache instead of network.
            this.patchScriptLoader(cache);

            // Step 3: pre-load all cached images as data URLs so CSS patches can replace
            // url() references synchronously without further network or cache async calls.
            const imageDataUrls = await this.buildImageDataUrls(cache);

            // Step 4: patch appendStyleSheet (skin/plugin CSS loaded as <link>) and
            // appendStyleText (toolbar icon CSS injected inline) so all CSS is served
            // from cache with image references replaced by data URLs.
            this.patchAppendStyleSheet(cache, imageDataUrls);
            this.patchAppendStyleText(imageDataUrls);

            // Step 5: CKEditor's richcombo floatPanel writes its iframe HTML directly
            // (not via appendStyleSheet), embedding <link rel="stylesheet"> tags for
            // contents.css and the skin.  Those link requests fail offline.
            // A MutationObserver catches each panel iframe as it's created and replaces
            // its <link> tags with inline <style> content served from cache.
            this.patchPanelIframes(cache, imageDataUrls);
        } catch (err) {
            reportError(err);
        }
    },

    async init() {
        if (marketplacePropertiesService.isOfflineResilienceForCkEditorEnabled() && !navigator.onLine) {
            // Device is offline — load ckeditor.js directly from Cache Storage as a blob URL.
            // Same-origin path requests (/player/file/ci/...) require the service worker to be
            // active and intercepting, which is not guaranteed in all environments. Reading from
            // the Cache Storage API and creating a blob URL is SW-independent and always works.
            // CKEDITOR_BASEPATH must be set before ckeditor.js executes so CKEditor resolves all
            // internal resource URLs (skins, plugins, dialogs) to the same-origin path that the
            // service worker serves from GENERAL_RESOURCES Cache Storage.
            window.CKEDITOR_BASEPATH = getSameOriginRoot();
            await this.loadCoreFromCache();
            return window.CKEDITOR;
        }
        return loadingTools.loadLibrary(this.getCoreFile().url, 'CKEDITOR');
    },

    async loadSpellcheckerBundle() {
        if (!window.WEBSPELLCHECKER) {
            return loadingTools.loadLibrary(`${WEBSPELLCHECKER_WEB_LATEST}wscbundle/wscbundle.js`, 'WEBSPELLCHECKER');
        }
        return Promise.resolve(window.WEBSPELLCHECKER);
    },
};

export default CKEditorLoader;
